import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Box, Typography, Card, CardContent, Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody } from "@mui/material";
import { LineChart as MuiLineChart } from "@mui/x-charts";
import ModernToggle from "../ModernToggle";

// Mini Trend Chart Component for Header Area
const MiniTrendChart = ({ statsData, isCumulativeTrend, height = 250, countLabel = "Count" }) => {
    const containerRef = React.useRef(null);
    const [chartWidth, setChartWidth] = React.useState(320);

    React.useEffect(() => {
        const updateWidth = () => {
            if (containerRef.current) {
                const width = containerRef.current.offsetWidth;
                setChartWidth(Math.max(width - 10, 200)); // Minimum 200px width
            }
        };

        updateWidth();
        window.addEventListener('resize', updateWidth);
        return () => window.removeEventListener('resize', updateWidth);
    }, []);

    if (!statsData || !statsData.trend || statsData.trend.length === 0) {
        return null;
    }

    const { trend } = statsData;
    const trendDates = trend.map(item => item.date.split('-')[2]);
    const fullDates = trend.map(item => {
        const dateObj = new Date(item.date);
        dateObj.setDate(dateObj.getDate() + 1);
        return dateObj.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    });
    const trendCounts = isCumulativeTrend
        ? trend.reduce((acc, item, index) => {
            acc.push((acc[index - 1] || 0) + item.count);
            return acc;
        }, [])
        : trend.map(item => item.count);

    return (
        <Box
            ref={containerRef}
            sx={{ height: '100%', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
            <MuiLineChart
                xAxis={[{
                    data: trendDates,
                    scaleType: 'point',
                    tickLabelStyle: {
                        fontSize: 9,
                        fill: '#9e9e9e',
                        fontFamily: 'Inter, Roboto, Helvetica, Arial, sans-serif'
                    },
                    valueFormatter: (value, context) => {
                        if (context.location === 'tooltip') {
                            const index = trendDates.indexOf(value);
                            return index !== -1 ? fullDates[index] : value;
                        }
                        return value;
                    }
                }]}
                yAxis={[{
                    label: countLabel,
                    labelStyle: {
                        fontSize: 8,
                        fill: '#9e9e9e',
                        fontFamily: 'Poppins, Inter, Roboto, Helvetica, Arial, sans-serif'
                    },
                    tickLabelStyle: {
                        fontSize: 9,
                        fill: '#9e9e9e',
                        fontFamily: 'Inter, Roboto, Helvetica, Arial, sans-serif'
                    }
                }]}
                series={[
                    {
                        data: trendCounts,
                        color: '#5B8DEF',
                        showMark: true,
                        area: true
                    }
                ]}
                width={chartWidth}
                height={height}
                margin={{ top: 10, bottom: 22, left: 35, right: 10 }}
                slotProps={{
                    legend: { hidden: true }
                }}
                sx={{
                    '& .MuiAreaElement-root': {
                        fill: 'url(#trendGradient)',
                        opacity: 0.15
                    },
                    '& .MuiMarkElement-root': {
                        fill: '#5B8DEF',
                        stroke: '#fff',
                        strokeWidth: 2,
                        r: 3
                    }
                }}
            />
        </Box>
    );
};

// Stats Tab Component - Trend Chart and Response Statistics Table
const StatsSection = ({ statsData }) => {
    const intl = useIntl();
    const [viewMode, setViewMode] = useState("table"); // "trend" or "table"
    const [isCumulativeTrend, setIsCumulativeTrend] = useState(false);

    if (!statsData) {
        return (
            <Card>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        <FormattedMessage id="statsSection.statistics" defaultMessage="Statistics" />
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        <FormattedMessage id="statsSection.no_data" defaultMessage="No statistics data available" />
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    // Calculate response statistics
    const emailsSent = statsData.emails_sent || statsData.recipients || 0;
    const responded = statsData.responded || statsData.completely_responded || 0;
    const notResponded = statsData.not_responded || (emailsSent - responded) || 0;
    const totalForPercentage = emailsSent > 0 ? emailsSent : 1;

    const responseStats = [
        {
            key: "emails_sent",
            count: emailsSent,
            percentage: 100
        },
        {
            key: "responded",
            count: responded,
            percentage: ((responded / totalForPercentage) * 100).toFixed(1)
        },
        {
            key: "not_responded",
            count: notResponded,
            percentage: ((notResponded / totalForPercentage) * 100).toFixed(1)
        }
    ];

    // Get trend data for chart
    const hasTrendData = statsData.trend && statsData.trend.length > 0;

    let startDate = '';
    let endDate = '';
    if (hasTrendData) {
        const startDateObj = new Date(statsData.trend[0]?.date);
        startDateObj.setDate(startDateObj.getDate() + 1);
        startDate = startDateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

        const endDateObj = new Date(statsData.trend[statsData.trend.length - 1]?.date);
        endDateObj.setDate(endDateObj.getDate() + 1);
        endDate = endDateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }

    return (
        <Card>
            <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                    <Typography variant="h6">
                        <FormattedMessage id="statsSection.response_statistics" defaultMessage="Response Statistics" />
                    </Typography>
                    <ModernToggle
                        leftLabel={intl.formatMessage({ id: "statsSection.trend_chart", defaultMessage: "Trend Chart" })}
                        rightLabel={intl.formatMessage({ id: "statsSection.table", defaultMessage: "Table" })}
                        checked={viewMode === "table"}
                        onChange={(checked) => setViewMode(checked ? "table" : "trend")}
                    />
                </Box>

                {viewMode === "trend" ? (
                    /* Trend Chart View */
                    <Box>
                        {hasTrendData ? (
                            <Box sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                justifyContent: 'center',
                                gap: 2,
                                alignItems: { xs: 'center', sm: 'stretch' }
                            }}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 1.5,
                                        textAlign: "center",
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                        width: { xs: '100%', sm: 170 },
                                        flexShrink: 0,
                                        background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
                                        borderRadius: 3,
                                        border: '1px solid #e0e0e0',
                                    }}
                                >
                                    <Box>
                                        <Typography sx={{ color: '#757575', letterSpacing: 1, fontSize: '0.55rem', fontWeight: 600, textTransform: 'uppercase' }}>
                                            <FormattedMessage id="statsSection.period" defaultMessage="Period" />
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#424242', lineHeight: 1.2, mt: 0.5 }}>
                                            {startDate}
                                        </Typography>
                                        <Typography sx={{ color: '#bdbdbd', fontSize: '0.7rem', lineHeight: 1 }}>
                                            <FormattedMessage id="statsSection.to" defaultMessage="to" />
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#424242', lineHeight: 1.2 }}>
                                            {endDate}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ mt: 1 }}>
                                        <ModernToggle
                                            leftLabel={intl.formatMessage({ id: "statsSection.daily", defaultMessage: "Daily" })}
                                            rightLabel={intl.formatMessage({ id: "statsSection.cumulative", defaultMessage: "Cumulative" })}
                                            checked={isCumulativeTrend}
                                            onChange={setIsCumulativeTrend}
                                            size="small"
                                        />
                                    </Box>
                                </Paper>
                                <Box sx={{
                                    flex: 1,
                                    width: { xs: '100%', sm: 'auto' },
                                    maxWidth: { xs: '100%', sm: 700, md: 800, lg: 900 },
                                    minWidth: { xs: 0, sm: 400, md: 500, lg: 600 },
                                    minHeight: { xs: 200, sm: 250, md: 300 },
                                    background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
                                    borderRadius: 3,
                                    border: '1px solid #e0e0e0',
                                    overflow: 'hidden'
                                }}>
                                    <MiniTrendChart
                                        statsData={statsData}
                                        isCumulativeTrend={isCumulativeTrend}
                                        height={250}
                                        countLabel={intl.formatMessage({ id: "statsSection.count", defaultMessage: "Count" })}
                                    />
                                </Box>
                            </Box>
                        ) : (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <Typography variant="body2" color="text.secondary">
                                    <FormattedMessage id="statsSection.no_trend_data" defaultMessage="No trend data available" />
                                </Typography>
                            </Box>
                        )}
                    </Box>
                ) : (
                    /* Table View */
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <TableContainer component={Paper} sx={{ maxWidth: 500, width: '100%' }}>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#1a365d' }}>
                                        <TableCell sx={{ fontWeight: 'bold', color: '#ffffff' }}>
                                            <FormattedMessage id="statsSection.metric" defaultMessage="Metric" />
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold', color: '#ffffff' }}>
                                            <FormattedMessage id="statsSection.count" defaultMessage="Count" />
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold', color: '#ffffff' }}>
                                            <FormattedMessage id="statsSection.percentage" defaultMessage="Percentage" />
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {responseStats.map((stat, index) => (
                                        <TableRow
                                            key={index}
                                            hover
                                            sx={{
                                                bgcolor: index % 2 === 0 ? '#f9f9f9' : 'white'
                                            }}
                                        >
                                            <TableCell sx={{ fontWeight: 500 }}>
                                                <FormattedMessage
                                                    id={`statsSection.${stat.key}`}
                                                    defaultMessage={stat.key.replace('_', ' ')}
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                {stat.count.toLocaleString()}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                                                    <Typography variant="body2">
                                                        {stat.percentage}%
                                                    </Typography>
                                                    <Box
                                                        sx={{
                                                            width: 60,
                                                            height: 8,
                                                            backgroundColor: '#e0e0e0',
                                                            borderRadius: 4,
                                                            overflow: 'hidden'
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: `${stat.percentage}%`,
                                                                height: '100%',
                                                                backgroundColor: stat.key === 'responded' ? '#4caf50' :
                                                                    stat.key === 'not_responded' ? '#f44336' : '#1976d2',
                                                                transition: 'width 0.3s ease'
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

export default StatsSection;